import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ButtonV2, Combobox, DialogV2, Flex, FormControl, IconV2, InputV2, Spinner, TextV2 } from '@withjoy/joykit';
import { Warning } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCurrencyCodeFromBrowserLocale } from '@shared/utils/currency/useCurrencyCodeFromBrowseLocale';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import { useCreditCardSetupTelemetry } from '../CreditCardSetUp.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { useGetCashFundCurrenciesQuery } from '@shared/utils/currency/cashFundCurrencies.graphql.generated';
import { buildCountryEntries, getCountryLabels, getCurrencyForCountry, resolveDefaultCountry, DEFAULT_COUNTRY_CODE } from '@shared/utils/currency/cashFundCountries';

const FORM_CONTROL_LABEL_OVERRIDES = {
  Label: { props: { typographyVariant: 'label3' as const } },
};

type InitHostFormProps = Readonly<{
  onSubmit: (firstName: string, lastName: string, currencyCode: string, countryCode: string) => Promise<void>;
  loading: boolean;
  registryCurrencyCode?: string;
  shippingCountryCode?: string;
}>;

type InitHostFormValues = Readonly<{
  firstName: string;
  lastName: string;
  countryCode: string;
  currencyCode: string;
}>;

export const InitHostForm: React.FC<InitHostFormProps> = ({ onSubmit, loading, registryCurrencyCode, shippingCountryCode }) => {
  const { tq } = useTranslation('yourRegistry');
  const { currentUser } = useAuth();
  const telemetry = useCreditCardSetupTelemetry();
  const [isI18nEnabled] = useIsFeatureEnabled('registryCashFundCurrency18nEnabled');
  const browserCurrencyCode = useCurrencyCodeFromBrowserLocale();
  const { data: currenciesData, loading: currenciesLoading } = useGetCashFundCurrenciesQuery({ batchMode: 'fast', fetchPolicy: 'cache-first' });

  const countryEntries = useMemo(() => buildCountryEntries(currenciesData?.getCashFundCurrencies.currencyDescriptors ?? []), [currenciesData]);

  const countryOptions = useMemo(() => {
    const labels = getCountryLabels(tq);
    return countryEntries.map(entry => ({
      value: entry.countryCode,
      label: labels[entry.countryCode] ?? entry.countryCode,
    }));
  }, [countryEntries, tq]);

  const activeOptions = useMemo(() => (isI18nEnabled ? countryOptions : countryOptions.filter(c => c.value === DEFAULT_COUNTRY_CODE)), [countryOptions, isI18nEnabled]);

  const defaultCountryCode = useMemo(
    () => (!isI18nEnabled ? DEFAULT_COUNTRY_CODE : resolveDefaultCountry(countryEntries, registryCurrencyCode, shippingCountryCode, browserCurrencyCode)),
    [isI18nEnabled, countryEntries, registryCurrencyCode, shippingCountryCode, browserCurrencyCode],
  );

  useMount(() => {
    telemetry.creditCardSetupDialog.enter('initHostForm');
  });

  const validationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string().required(tq`First name is required`),
        lastName: Yup.string().required(tq`Last name is required`),
        countryCode: Yup.string().required(tq`Country is required`),
        currencyCode: Yup.string().required(),
      }),
    [tq],
  );

  const formik = useFormik<InitHostFormValues>({
    enableReinitialize: true,
    initialValues: {
      firstName: currentUser.profile?.firstName || '',
      lastName: currentUser.profile?.lastName || '',
      countryCode: defaultCountryCode,
      currencyCode: getCurrencyForCountry(countryEntries, defaultCountryCode),
    },
    validationSchema,
    onSubmit: async values => {
      telemetry.initHostFormSubmitted();
      await onSubmit(values.firstName, values.lastName, values.currencyCode, values.countryCode);
    },
  });

  const handleCountryChange = (value: string) => {
    formik.setFieldValue('countryCode', value);
    formik.setFieldValue('currencyCode', getCurrencyForCountry(countryEntries, value));
  };

  const selectedCountryCode = formik.values.countryCode;
  const selectedCountryLabel = countryOptions.find(c => c.value === selectedCountryCode)?.label ?? selectedCountryCode;
  const derivedCurrency = formik.values.currencyCode;
  const hasCurrencyMismatch = isI18nEnabled && registryCurrencyCode && derivedCurrency && derivedCurrency !== registryCurrencyCode;

  if (currenciesLoading || (isI18nEnabled && !registryCurrencyCode)) {
    return (
      <DialogV2.Body>
        <Flex
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Spinner />
        </Flex>
      </DialogV2.Body>
    );
  }

  return (
    <DialogV2.Body>
      <form onSubmit={formik.handleSubmit}>
        <Flex
          flexDirection="column"
          gap={6}
          padding={6}
        >
          <TextV2
            typographyVariant="hed4"
            textAlign="center"
          >
            {tq`Who is your cash fund contact?`}
          </TextV2>
          <FormControl
            label={tq`First Name`}
            isInvalid={formik.touched.firstName && !!formik.errors.firstName}
            error={formik.errors.firstName}
            overrides={FORM_CONTROL_LABEL_OVERRIDES}
          >
            <InputV2
              name="firstName"
              placeholder={tq`First Name`}
              value={formik.values.firstName}
              onChange={e => formik.setFieldValue('firstName', e.target.value)}
              onBlur={formik.handleBlur}
            />
          </FormControl>
          <FormControl
            label={tq`Last Name`}
            isInvalid={formik.touched.lastName && !!formik.errors.lastName}
            error={formik.errors.lastName}
            overrides={FORM_CONTROL_LABEL_OVERRIDES}
          >
            <InputV2
              name="lastName"
              placeholder={tq`Last Name`}
              value={formik.values.lastName}
              onChange={e => formik.setFieldValue('lastName', e.target.value)}
              onBlur={formik.handleBlur}
            />
          </FormControl>
          <FormControl
            label={tq`Country`}
            overrides={FORM_CONTROL_LABEL_OVERRIDES}
          >
            <Combobox
              options={activeOptions}
              value={formik.values.countryCode}
              onValueChange={handleCountryChange}
              searchable={false}
            />
          </FormControl>
          {isI18nEnabled && (
            <FormControl
              label={tq`Currency`}
              overrides={FORM_CONTROL_LABEL_OVERRIDES}
            >
              <Combobox
                options={[{ value: derivedCurrency, label: `${derivedCurrency} (${getCurrencySymbolFromCurrencyCode(derivedCurrency)})` }]}
                value={derivedCurrency}
                searchable={false}
                disabled
              />
            </FormControl>
          )}
          {hasCurrencyMismatch && (
            <Flex
              padding={5}
              borderRadius={3}
              borderWidth="1px"
              borderStyle="solid"
              borderColor="info3"
            >
              <IconV2
                color="info5"
                marginRight={2}
              >
                <Warning />
              </IconV2>
              <TextV2
                typographyVariant="label2"
                color="mono14"
              >
                {tq`Selecting ${selectedCountryLabel} will set your registry currency to ${derivedCurrency}. Guests will see ${derivedCurrency} prices when contributing to your cash fund and any externally added items. This does not affect Joy catalog items. Your currency will be locked once saved.`}
              </TextV2>
            </Flex>
          )}
          <Flex
            justifyContent="flex-end"
            gap={3}
            marginTop={4}
          >
            <ButtonV2
              type="submit"
              intent="neutral"
              shape="rounded"
              fullWidth
              loading={formik.isSubmitting || loading}
            >
              {tq`Save`}
            </ButtonV2>
          </Flex>
          <Flex
            marginTop={4}
            justifyContent="center"
          >
            <TextV2
              as="a"
              href="https://stripe.com/legal/connect-account"
              target="_blank"
              rel="noopener noreferrer"
              color="mono8"
              typographyVariant="label2"
              textAlign="center"
              textDecoration="underline"
            >
              {tq`By continuing, you agree to Stripe's terms of service.`}
            </TextV2>
          </Flex>
        </Flex>
      </form>
    </DialogV2.Body>
  );
};
